import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeftIcon,
  PencilIcon,
  ChatBubbleLeftRightIcon,
  PhotoIcon,
  VideoCameraIcon,
  PaperClipIcon,
  PaperAirplaneIcon,
  CalendarIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  ArrowPathIcon,
  ChartBarIcon,
  BookmarkIcon,
} from '@heroicons/react/24/outline';
import VisionSavedAdvice from './visions/VisionSavedAdvice';
import axios from 'axios';
import EnhancedVisionAIAssistant from './EnhancedVisionAIAssistant';
import VisionTrajectoryChart from './VisionTrajectoryChart';
import VisionActionList from './VisionActionList';
import VisionStrategyChart from './VisionStrategyChart';
import StrategyCardContent from './common/StrategyCardContent';
import VisionStrategyCard from './visions/VisionStrategyCard';
import { AuraFormattedText } from './common/AuraFormattedText';
import CardSaveButton from './common/CardSaveButton';
import ShareProgressButton from './ShareProgressButton';
import VisionImagePlate from './common/VisionImagePlate';
import { useUpgrade } from '../context/UpgradeContext';
import ProBadge from './upgrade/ProBadge';
import { useCurrency } from '../context/CurrencyContext';

const VALID_TABS = new Set(['journal', 'progress', 'chat', 'saved']);

const VisionDetailPage = ({ vision, onBack, user, onVisionUpdate }) => {
  const { formatAmount } = useCurrency();
  const { permissions, openUpgrade } = useUpgrade();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(
    VALID_TABS.has(tabFromUrl) ? tabFromUrl : 'journal',
  );

  useEffect(() => {
    if (VALID_TABS.has(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [tabFromUrl]);

  const handleTabChange = (id) => {
    setActiveTab(id);
    if (location.pathname.includes('/vision/')) {
      navigate({ pathname: location.pathname, search: `?tab=${id}` }, { replace: true });
    }
  };
  const [journalEntries, setJournalEntries] = useState([]);
  const [newJournalEntry, setNewJournalEntry] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [targetDate, setTargetDate] = useState(vision.target_date || '');
  const [progressAnalysis, setProgressAnalysis] = useState(null);
  const [achievementStrategy, setAchievementStrategy] = useState(null);
  const [showUpdateProgressModal, setShowUpdateProgressModal] = useState(false);
  const [showMarkAchievedModal, setShowMarkAchievedModal] = useState(false);
  const [newProgressAmount, setNewProgressAmount] = useState('');
  const [localVision, setLocalVision] = useState(vision);
  const [imageRegenerating, setImageRegenerating] = useState(false);

  // Keep local vision in sync when prop changes (e.g. after refetch)
  useEffect(() => {
    setLocalVision(vision);
  }, [vision]);

  // Poll while cover image generates in background
  useEffect(() => {
    if (localVision?.image_status !== 'pending' || localVision?.image_url) return undefined;
    const visionId = localVision.vision_id;
    let attempts = 0;
    let cancelled = false;

    const tick = async () => {
      attempts += 1;
      try {
        const res = await axios.get(`/api/visions/${visionId}`, { timeout: 10000 });
        const v = res.data;
        if (cancelled) return true;
        if (v?.image_url) {
          setLocalVision((prev) => ({
            ...prev,
            image_url: v.image_url,
            image_status: v.image_status,
          }));
          onVisionUpdate?.();
          return true;
        }
        if (v?.image_status === 'failed' || v?.image_status === 'skipped') return true;
      } catch {
        /* retry */
      }
      return attempts >= 30;
    };

    const id = setInterval(async () => {
      if (await tick()) clearInterval(id);
    }, 3000);
    tick();

    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [localVision?.vision_id, localVision?.image_status, localVision?.image_url, onVisionUpdate]);

  // Prefill progress modal when opening
  useEffect(() => {
    if (showUpdateProgressModal) {
      setNewProgressAmount(String(localVision.current_amount ?? ''));
    }
  }, [showUpdateProgressModal, localVision.current_amount]);
  const [isGeneratingStrategy, setIsGeneratingStrategy] = useState(false);
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);
  const [portfolioData, setPortfolioData] = useState(null);
  const [cashflowData, setCashflowData] = useState(null);
  const [profitRoutingRules, setProfitRoutingRules] = useState([]);

  useEffect(() => {
    if (vision) {
      loadJournalEntries();
      loadProgressAnalysis();
      loadAchievementStrategy();
      loadPortfolioData();
      loadCashflowData();
      axios.get('/api/aura/profit-routing').then((r) => setProfitRoutingRules(r.data?.rules || [])).catch(() => setProfitRoutingRules([]));
    }
  }, [vision]);
  
  const loadPortfolioData = async () => {
    try {
      const response = await axios.get('/api/portfolio/accounts');
      if (response.data) {
        setPortfolioData(response.data);
      }
    } catch (error) {
      console.log('Portfolio data not available:', error.message);
    }
  };
  
  const loadCashflowData = async () => {
    try {
      const [metricsResponse, expensesResponse] = await Promise.all([
        axios.get('/api/cashflow/metrics').catch(() => null),
        axios.get('/api/cashflow/essential-expenses').catch(() => null)
      ]);
      
      let cashflow = {};
      if (metricsResponse?.data?.income_pattern) {
        const ip = metricsResponse.data.income_pattern;
        const monthly = ip.frequency === 'weekly' 
          ? ip.average_amount * 4.33
          : ip.frequency === 'bi_weekly'
          ? ip.average_amount * 2.17
          : ip.average_amount;
        cashflow.monthly_income = monthly;
      }
      
      if (expensesResponse?.data?.expenses) {
        const totalEssentials = Object.values(expensesResponse.data.expenses).reduce(
          (sum, val) => sum + (parseFloat(val) || 0), 0
        );
        cashflow.monthly_essential_expenses = totalEssentials;
        cashflow.monthly_savings = (cashflow.monthly_income || 0) - totalEssentials;
        cashflow.investable_amount = Math.max(0, cashflow.monthly_savings);
      }
      
      setCashflowData(cashflow);
    } catch (error) {
      console.log('Cashflow data not available:', error.message);
    }
  };

  const loadJournalEntries = async () => {
    try {
      const res = await axios.get(`/api/visions/${vision.vision_id}/journal`, { timeout: 15000 });
      setJournalEntries(res.data.entries || []);
    } catch (err) {
      console.error('Failed to load journal entries:', err);
    }
  };

  const loadProgressAnalysis = async () => {
    try {
      const res = await axios.get(`/api/visions/${vision.vision_id}/progress-analysis`);
      if (res.data.success) {
        setProgressAnalysis(res.data);
      }
    } catch (error) {
      console.error('Error loading progress analysis:', error);
    }
  };

  const loadAchievementStrategy = async () => {
    try {
      if (vision.achievement_strategy) {
        setAchievementStrategy(vision.achievement_strategy);
      }
    } catch (error) {
      console.error('Error loading achievement strategy:', error);
    }
  };

  const generateAchievementStrategy = async () => {
    if (isGeneratingStrategy) {
      console.log('Strategy generation already in progress');
      return;
    }

    if (!permissions?.can_access_strategy_card) {
      openUpgrade('strategy_card');
      return;
    }

    setIsGeneratingStrategy(true);
    try {
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found');
        alert('Please log in to generate strategy');
        setIsGeneratingStrategy(false);
        return;
      }

      console.log('Generating strategy for vision:', vision.vision_id);

      const res = await axios.post(
        `/api/visions/${vision.vision_id}/achievement-strategy`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 60000 // Increased to 60 seconds for AI generation
        }
      );

      console.log('Strategy response:', res.data);

      if (res.data.success) {
        setAchievementStrategy(res.data.strategy || { card: res.data.card });
        alert('Strategy generated successfully!');
      } else {
        alert('Failed to generate strategy. Please try again.');
      }
    } catch (error) {
      console.error('Error generating strategy:', error);
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403 && typeof detail === 'object' && detail?.error === 'strategy_card_gated') {
        openUpgrade('strategy_card');
        return;
      }
      const errorMsg = typeof detail === 'object' ? detail?.message : detail || error.message || 'Failed to generate strategy';
      alert(`Error: ${errorMsg}`);
    } finally {
      setIsGeneratingStrategy(false);
    }
  };

  const updateTargetDate = async () => {
    if (isUpdatingDate) {
      console.log('Date update already in progress');
      return;
    }

    setIsUpdatingDate(true);
    try {
      if (!targetDate) {
        alert('Please select a target date');
        setIsUpdatingDate(false);
        return;
      }

      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found');
        alert('Please log in to update target date');
        setIsUpdatingDate(false);
        return;
      }

      console.log('Updating target date for vision:', vision.vision_id, 'to:', targetDate);

      await axios.put(
        `/api/visions/${vision.vision_id}`,
        { target_date: targetDate },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          timeout: 15000
        }
      );

      // Reload progress analysis after updating target date
      await loadProgressAnalysis();
      alert('Target date updated successfully!');
    } catch (error) {
      console.error('Error updating target date:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to update target date';
      alert(`Error: ${errorMsg}`);
    } finally {
      setIsUpdatingDate(false);
    }
  };

  const handleJournalSubmit = async (e) => {
    e.preventDefault();
    if (!newJournalEntry.trim()) return;

    setIsTyping(true);
    try {
      const res = await axios.post(
        `/api/visions/${vision.vision_id}/journal`,
        { 
          content: newJournalEntry,
          vision_id: vision.vision_id,
          attachments: attachedFiles.map(f => ({ id: f.id, name: f.name, type: f.type, url: f.url }))
        },
        { timeout: 15000 }
      );
      
      if (res.data.success) {
        setNewJournalEntry('');
        setAttachedFiles([]);
        loadJournalEntries();
      }
    } catch (err) {
      console.error('Failed to save journal entry:', err);
    } finally {
      setIsTyping(false);
    }
  };

  const handleUpdateProgress = async () => {
    if (!newProgressAmount && newProgressAmount !== 0) return;

    try {
      const res = await axios.put(
        `/api/visions/${vision.vision_id}`,
        { current_amount: parseFloat(newProgressAmount) },
        { timeout: 15000 }
      );
      
      if (res.data.success !== false) {
        setShowUpdateProgressModal(false);
        setNewProgressAmount('');
        setLocalVision(prev => ({ ...prev, current_amount: parseFloat(newProgressAmount) }));
        if (typeof onVisionUpdate === 'function') {
          onVisionUpdate();
        } else {
          window.location.reload();
        }
      }
    } catch (err) {
      console.error('Failed to update progress:', err);
    }
  };

  const handleMarkAchieved = async () => {
    try {
      const res = await axios.put(
        `/api/visions/${vision.vision_id}/archive`,
        {},
        { timeout: 15000 }
      );
      
      if (res.data.success) {
        setShowMarkAchievedModal(false);
        onBack();
      }
    } catch (err) {
      console.error('Failed to mark as achieved:', err);
    }
  };

  const handleFileInputChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    
    // Simulate file processing
    setTimeout(() => {
      const newFiles = files.map((file, index) => ({
        id: `file-${Date.now()}-${index}`,
        name: file.name,
        type: file.type,
        url: URL.createObjectURL(file)
      }));
      
      setAttachedFiles(prev => [...prev, ...newFiles]);
      setIsUploading(false);
    }, 1000);
  };

  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const formatCurrency = (amount) => formatAmount(amount);

  const displayVision = localVision || vision;
  const progressPercentage = displayVision.target_amount > 0 
    ? Math.min(100, ((displayVision.current_amount || 0) / displayVision.target_amount) * 100)
    : 0;

  if (!vision) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-display text-h1 text-primary mb-4">Vision not found</h2>
          <button type="button" onClick={onBack} className="btn-primary">
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone">
      <header className="border-b border-hairline bg-bone-raised">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button type="button" onClick={onBack} className="btn-ghost p-2" aria-label="Back to visions">
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="font-display text-h1 text-primary">{vision.title}</h1>
              {vision.description && (
                <p className="text-body text-secondary mt-1">{vision.description}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {progressPercentage >= 100 && (
              <button
                type="button"
                onClick={() => setShowMarkAchievedModal(true)}
                className="btn-primary flex items-center gap-2"
              >
                <TrophyIcon className="w-4 h-4" />
                Mark achieved
              </button>
            )}
            {(progressPercentage >= 25 ||
              progressPercentage >= 50 ||
              progressPercentage >= 75 ||
              progressPercentage >= 100) && (
              <ShareProgressButton
                entityType="vision"
                entityId={vision.vision_id}
                entityData={{
                  title: vision.title,
                  current_amount: displayVision.current_amount || 0,
                  target_amount: displayVision.target_amount,
                }}
                user={user}
              />
            )}
          </div>
        </div>
      </header>

      <div className="border-b border-hairline bg-pine-tint">
        <div className="max-w-7xl mx-auto px-6 py-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <p className="text-label text-secondary mb-1">Amount saved toward this vision</p>
            <p className="figure text-display text-primary">
              {formatCurrency(displayVision.current_amount)}{' '}
              <span className="text-h3 text-secondary">of {formatCurrency(displayVision.target_amount)}</span>
            </p>
          </div>
          <button type="button" onClick={() => setShowUpdateProgressModal(true)} className="btn-secondary flex items-center gap-2">
            <PencilIcon className="w-4 h-4" />
            Update amount saved
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        {/* Vision Overview */}
        <section className="surface-panel p-6 mb-8">
          <div className="mb-6 flex flex-col sm:flex-row gap-4 sm:items-end sm:justify-between">
            <div className="max-w-md flex-1">
              <VisionImagePlate
                src={localVision.image_url}
                alt={localVision.title}
              />
              {localVision.image_status === 'pending' && (
                <p className="text-label text-secondary mt-2">Cover image generating…</p>
              )}
            </div>
            <button
              type="button"
              disabled={imageRegenerating}
              onClick={async () => {
                setImageRegenerating(true);
                try {
                  await axios.post(
                    `/api/visions/${localVision.vision_id}/regenerate-image`,
                    {},
                    { timeout: 15000 }
                  );
                  for (let i = 0; i < 25; i += 1) {
                    await new Promise((r) => setTimeout(r, 3000));
                    const res = await axios.get(
                      `/api/visions/${localVision.vision_id}`,
                      { timeout: 10000 }
                    );
                    const v = res.data;
                    if (v?.image_url) {
                      setLocalVision((prev) => ({
                        ...prev,
                        image_url: v.image_url,
                        image_status: v.image_status,
                      }));
                      onVisionUpdate?.();
                      break;
                    }
                    if (v?.image_status === 'failed') break;
                  }
                } catch (err) {
                  console.error('Regenerate image failed', err);
                } finally {
                  setImageRegenerating(false);
                }
              }}
              className="btn-ghost flex items-center gap-2 shrink-0"
            >
              <ArrowPathIcon className={`w-4 h-4 ${imageRegenerating ? 'animate-spin' : ''}`} />
              {imageRegenerating ? 'Regenerating…' : 'Regenerate cover'}
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-6 mb-6 border-b border-hairline">
            <div>
              <p className="text-label text-secondary mb-1">Current amount</p>
              <p className="figure text-h2 text-primary">{formatCurrency(displayVision.current_amount)}</p>
            </div>
            <div>
              <p className="text-label text-secondary mb-1">Target amount</p>
              <p className="figure text-h2 text-primary">{formatCurrency(displayVision.target_amount)}</p>
            </div>
            <div>
              <p className="text-label text-secondary mb-1">Progress</p>
              <p className="figure text-h2 text-pine">{Math.round(progressPercentage)}%</p>
            </div>
          </div>
          
          <div>
            <div className="flex justify-between text-label text-secondary mb-2">
              <span>Progress</span>
              <span className="figure text-primary">{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full h-1.5 rounded-full bg-hairline overflow-hidden">
              <div className="h-full bg-pine rounded-full transition-all duration-500" style={{ width: `${progressPercentage}%` }} />
            </div>
          </div>

          {vision.target_date && (
            <div className="mt-4 flex items-center text-body text-secondary">
              <CalendarIcon className="w-4 h-4 mr-2" />
              <span>Target Date: {new Date(vision.target_date).toLocaleDateString()}</span>
            </div>
          )}

          {progressAnalysis && progressAnalysis.recommendations && (
            <div className="mt-4 pt-4 border-t border-hairline">
              <p className="text-label text-secondary mb-1">Aura suggests</p>
              <p className="text-body text-primary mt-1">
                {progressAnalysis.recommendations.timeline_adjustment}
                {!progressAnalysis.progress_metrics?.is_on_track &&
                  progressAnalysis.recommendations.increase_monthly > 0 && (
                    <span className="block mt-1 text-secondary">
                      Saving about {formatAmount(Math.round(progressAnalysis.recommendations.increase_monthly))}/mo could help you stay on track.
                    </span>
                  )}
              </p>
            </div>
          )}

          {(() => {
            const routingToThisVision = (profitRoutingRules || []).filter(
              (r) => (r.allocations || []).some((a) => a.vision_id === vision?.vision_id && (Number(a.percentage) || 0) > 0)
            );
            if (routingToThisVision.length === 0) return null;
            return (
              <div className="mt-4 p-3 rounded-lg border border-hairline bg-bone">
                <span className="text-label text-secondary">Profit routing to this vision</span>
                <ul className="text-body text-secondary mt-1 space-y-1">
                  {routingToThisVision.map((rule) => {
                    const alloc = (rule.allocations || []).find((a) => a.vision_id === vision.vision_id);
                    const pct = alloc ? Number(alloc.percentage) : 0;
                    const source = rule.source_id || rule.source_type || 'Recommendation';
                    return <li key={rule.source_id}>{(pct > 0 ? `${pct}%` : '')} of profits from “{source}” → here</li>;
                  })}
                </ul>
              </div>
            );
          })()}
        </section>

        {/* Tabs */}
        <div className="surface-panel overflow-hidden">
          <div className="flex border-b border-hairline px-2">
            {[
              { id: 'journal', label: 'Journal', icon: PencilIcon },
              { id: 'progress', label: 'Progress', icon: TrophyIcon },
              { id: 'chat', label: 'AI assistant', icon: ChatBubbleLeftRightIcon },
              { id: 'saved', label: 'Saved', icon: BookmarkIcon },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => handleTabChange(id)}
                aria-selected={activeTab === id}
                className={`flex items-center gap-2 px-5 py-4 text-body font-medium transition-colors duration-300 ${
                  activeTab === id ? 'nav-tab-active' : 'nav-tab'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <AnimatePresence mode="wait">
            {activeTab === 'journal' ? (
              <motion.div
                key="journal"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="p-6"
              >
                {/* Journal Entry Form */}
                <div className="surface-panel p-6 mb-6">
                  <h3 className="font-display text-h3 text-primary mb-4">Write a journal entry</h3>
                  <form onSubmit={handleJournalSubmit}>
                    <textarea
                      value={newJournalEntry}
                      onChange={(e) => setNewJournalEntry(e.target.value)}
                      placeholder="Reflect on your progress, challenges, or insights about this vision..."
                      className="w-full h-32 input-editorial resize-none"
                    />
                    
                    {/* File Upload */}
                    <div className="mt-4">
                      <input
                        type="file"
                        id="file-upload"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleFileInputChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="file-upload"
                        className="btn-secondary inline-flex items-center gap-2 cursor-pointer"
                      >
                        <PhotoIcon className="w-4 h-4" />
                        <span>Add Images / Videos</span>
                      </label>
                      {isUploading && (
                        <div className="ml-4 inline-flex items-center gap-2 text-secondary">
                          <div className="w-4 h-4 border-2 border-hairline border-t-pine rounded-full animate-spin" />
                          <span>Uploading…</span>
                        </div>
                      )}
                    </div>

                    {/* Attached Files Preview */}
                    {attachedFiles.length > 0 && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-label text-secondary">Attached files</span>
                          <button
                            type="button"
                            onClick={() => setAttachedFiles([])}
                            className="text-label text-faint hover:text-secondary"
                          >
                            Clear all
                          </button>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {attachedFiles.map((file) => (
                            <div key={file.id} className="flex items-center gap-2 border border-hairline rounded-md p-2 bg-bone-raised">
                              {file.type?.startsWith('image/') ? (
                                <img src={file.url} alt={file.name} className="w-8 h-8 object-cover rounded-md" />
                              ) : file.type?.startsWith('video/') ? (
                                <VideoCameraIcon className="w-8 h-8 text-secondary" />
                              ) : (
                                <PaperClipIcon className="w-8 h-8 text-secondary" />
                              )}
                              <span className="text-label text-secondary truncate max-w-20">{file.name}</span>
                              <button
                                type="button"
                                onClick={() => removeAttachedFile(file.id)}
                                className="text-faint hover:text-secondary text-label"
                                aria-label="Remove file"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex justify-end mt-4">
                      <button
                        type="submit"
                        disabled={!newJournalEntry.trim() || isTyping}
                        className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isTyping ? 'Saving...' : 'Save Entry'}
                      </button>
                    </div>
                  </form>
                </div>

                {/* Journal Entries */}
                <div className="space-y-4">
                  {journalEntries.length === 0 ? (
                    <div className="text-center py-12">
                      <PencilIcon className="w-12 h-12 text-faint mx-auto mb-4" />
                      <h3 className="font-display text-h3 text-primary mb-2">No journal entries yet</h3>
                      <p className="text-body text-secondary">Start documenting your journey toward this vision</p>
                    </div>
                  ) : (
                    journalEntries.map((entry) => {
                      // Parse entry content if it's JSON string
                      let displayContent = '';
                      let displayTitle = '';
                      let displayDate = entry.created_at;
                      
                      try {
                        if (typeof entry.content === 'string' && (entry.content.startsWith('{') || entry.content.startsWith('['))) {
                          const parsed = JSON.parse(entry.content);
                          // Try multiple field names for content
                          displayContent = parsed.description || parsed.content || parsed.text || parsed.message || entry.content;
                          displayTitle = parsed.title || '';
                          // Extract other fields if available
                          if (parsed.recommendations && Array.isArray(parsed.recommendations)) {
                            displayContent += '\n\nRecommendations:\n' + parsed.recommendations.map((rec, i) => `${i + 1}. ${rec}`).join('\n');
                          }
                          if (parsed.action_items && Array.isArray(parsed.action_items)) {
                            displayContent += '\n\nAction items:\n' + parsed.action_items.map((item, i) => `${i + 1}. ${item}`).join('\n');
                          }
                        } else if (typeof entry === 'string') {
                          const parsed = JSON.parse(entry);
                          displayContent = parsed.description || parsed.content || parsed.text || parsed.message || entry;
                          displayTitle = parsed.title || '';
                          displayDate = parsed.created_at || parsed.date || displayDate;
                        } else {
                          displayContent = entry.content || entry.description || entry.text || entry.message || 'No content';
                        }
                      } catch (e) {
                        displayContent = entry.content || entry.description || entry.text || String(entry);
                      }
                      
                      return (
                        <div key={entry.entry_id || entry._id} className="surface-panel p-6 transition-colors duration-300 hover:bg-bone">
                          <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-pine" />
                              <span className="text-label text-secondary">
                                {displayDate ? new Date(displayDate).toLocaleDateString('en-US', { 
                                  month: 'short', 
                                  day: 'numeric', 
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'Recent'}
                              </span>
                            </div>
                            <span className="text-label text-faint px-2 py-0.5 border border-hairline rounded-md">Entry</span>
                          </div>
                          
                          {displayTitle && (
                            <h4 className="font-display text-h3 text-primary mb-2">{displayTitle}</h4>
                          )}
                          
                          <div className="text-body text-primary whitespace-pre-wrap leading-relaxed">{displayContent}</div>
                          
                          {entry.attachments && entry.attachments.length > 0 && (
                            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                              {entry.attachments.map((attachment, idx) => (
                                <div key={attachment.id || idx} className="border border-hairline rounded-md p-2 bg-bone-raised">
                                  {attachment.type?.startsWith('image/') ? (
                                    <img 
                                      src={attachment.url} 
                                      alt={attachment.name}
                                      className="w-full h-24 object-cover rounded"
                                    />
                                  ) : attachment.type?.startsWith('video/') ? (
                                    <video 
                                      src={attachment.url} 
                                      controls
                                      className="w-full h-24 rounded"
                                    />
                                  ) : (
                                    <div className="flex items-center space-x-2">
                                      <PaperClipIcon className="w-4 h-4 text-secondary" />
                                      <span className="text-label truncate text-secondary">{attachment.name}</span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </motion.div>
            ) : activeTab === 'progress' ? (
              <motion.div
                key="progress"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="p-6 space-y-6"
              >
                {/* Target Date Section */}
                <div className="surface-panel p-6">
                  <h3 className="font-display text-h3 text-primary mb-4 flex items-center gap-2">
                    <CalendarIcon className="w-5 h-5 text-secondary" />
                    Target date and timeline
                  </h3>
                  <div className="flex flex-wrap items-center gap-4">
                    <input
                      type="date"
                      value={targetDate}
                      onChange={(e) => setTargetDate(e.target.value)}
                      className="input-editorial"
                    />
                    <button
                      type="button"
                      onClick={updateTargetDate}
                      disabled={isUpdatingDate}
                      className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isUpdatingDate ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Date'
                      )}
                    </button>
                  </div>
                </div>

                {/* Progress Analysis */}
                {progressAnalysis && (
                  <div className="surface-panel p-6">
                    <h3 className="font-display text-h3 text-primary mb-4 flex items-center gap-2">
                      <CurrencyDollarIcon className="w-5 h-5 text-secondary" />
                      Progress analysis
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <p className="text-label text-secondary mb-1">Progress</p>
                        <p className="figure text-h2 text-primary">
                          {progressAnalysis.progress_metrics.progress_percentage.toFixed(1)}%
                        </p>
                        <p className="text-body text-secondary figure">
                          {formatAmount(progressAnalysis.progress_metrics.current_amount)} / {formatAmount(progressAnalysis.progress_metrics.target_amount)}
                        </p>
                      </div>
                      <div>
                        <p className="text-label text-secondary mb-1">Required monthly</p>
                        <p className="figure text-h2 text-primary">
                          {formatAmount(progressAnalysis.progress_metrics.required_monthly)}
                        </p>
                        <p className={`text-body ${progressAnalysis.progress_metrics.is_on_track ? 'text-pine' : 'text-warning'}`}>
                          {progressAnalysis.progress_metrics.is_on_track ? 'On track' : 'Needs attention'}
                        </p>
                      </div>
                    </div>
                    {progressAnalysis.progress_metrics.time_remaining_days && (
                      <div className="mt-4 pt-4 border-t border-hairline">
                        <p className="text-label text-secondary mb-1">Time remaining</p>
                        <p className="figure text-h3 text-primary">
                          {progressAnalysis.progress_metrics.time_remaining_days} days
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Vision Trajectory Chart */}
                <VisionTrajectoryChart 
                  vision={vision} 
                  portfolio={portfolioData}
                  cashflow={cashflowData}
                />

                {/* Strategic Actions */}
                <VisionActionList vision={vision} />

                {/* 3-Month Strategy Summary */}
                <div className="surface-panel p-6 border-t-2 border-t-pine">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-display text-h3 text-primary mb-1 inline-flex items-center gap-2 flex-wrap">
                        3-Month Strategy
                        {!permissions?.can_access_strategy_card && (
                          <ProBadge feature="strategy_card" />
                        )}
                      </h3>
                      <p className="text-body text-secondary">Simple, actionable plan for the next 3 months</p>
                    </div>
                    <button
                      type="button"
                      onClick={generateAchievementStrategy}
                      disabled={isGeneratingStrategy}
                      className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isGeneratingStrategy ? (
                        <>
                          <ArrowPathIcon className="w-4 h-4 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <ArrowPathIcon className="w-4 h-4" />
                          {achievementStrategy ? 'Refresh Strategy' : 'Generate Strategy'}
                        </>
                      )}
                    </button>
                  </div>
                  {achievementStrategy ? (
                    <div className="space-y-6">
                      <VisionStrategyChart 
                        vision={vision}
                        cashflow={cashflowData}
                        portfolio={portfolioData}
                      />

                      {achievementStrategy.plan ? (
                        <>
                          <div className="flex justify-end mb-2">
                            <CardSaveButton
                              card={
                                achievementStrategy.card || {
                                  type: 'strategy',
                                  title:
                                    achievementStrategy.structured?.title || '3-Month Strategy',
                                  horizon:
                                    achievementStrategy.structured?.horizon || '3 months',
                                  steps: achievementStrategy.structured?.steps || [],
                                  rationale: achievementStrategy.structured?.rationale || '',
                                }
                              }
                              sourceSurface="vision"
                              visionId={vision.vision_id}
                              showLabel
                            />
                          </div>
                          <VisionStrategyCard
                            visionId={vision.vision_id}
                            plan={achievementStrategy.plan}
                          />
                        </>
                      ) : (achievementStrategy.card ||
                        achievementStrategy.structured?.steps?.length > 0) ? (
                        <>
                          <div className="flex justify-end mb-2">
                            <CardSaveButton
                              card={
                                achievementStrategy.card || {
                                  type: 'strategy',
                                  title:
                                    achievementStrategy.structured?.title || '3-Month Strategy',
                                  horizon:
                                    achievementStrategy.structured?.horizon || '3 months',
                                  steps: achievementStrategy.structured?.steps || [],
                                  rationale: achievementStrategy.structured?.rationale || '',
                                }
                              }
                              sourceSurface="vision"
                              visionId={vision.vision_id}
                              showLabel
                            />
                          </div>
                          <StrategyCardContent
                            card={
                              achievementStrategy.card || {
                                type: 'strategy',
                                title:
                                  achievementStrategy.structured?.title || '3-Month Strategy',
                                horizon: achievementStrategy.structured?.horizon || '3 months',
                                steps: achievementStrategy.structured?.steps || [],
                                rationale: achievementStrategy.structured?.rationale || '',
                              }
                            }
                          />
                        </>
                      ) : null}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <ChartBarIcon className="w-12 h-12 mx-auto mb-4 text-faint" />
                      <p className="text-body text-primary mb-2">Generate your personalized 3-month strategy</p>
                      <p className="text-label text-secondary">
                        We will create a simple plan tailored to your goal
                      </p>
                    </div>
                  )}
                </div>
              </motion.div>
            ) : activeTab === 'chat' ? (
              <motion.div
                key="chat"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="flex-1 min-h-[600px] flex flex-col"
              >
                <EnhancedVisionAIAssistant
                  vision={vision}
                  user={user || { user_id: vision.user_id || 'unknown' }}
                  cashflowData={cashflowData}
                  portfolioData={portfolioData}
                  achievementStrategy={achievementStrategy}
                  onStrategyUpdated={(card) => {
                    if (card) {
                      const next = {
                        card,
                        structured: {
                          title: card.title,
                          horizon: card.horizon,
                          steps: card.steps,
                          rationale: card.rationale,
                        },
                      };
                      setAchievementStrategy(next);
                      setLocalVision((v) => ({ ...v, achievement_strategy: next }));
                      onVisionUpdate?.();
                    }
                  }}
                />
              </motion.div>
            ) : (
              <motion.div
                key="saved"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="p-6"
              >
                <VisionSavedAdvice visionId={vision.vision_id} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Update Progress Modal */}
      {showUpdateProgressModal && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-[1000]">
          <div className="surface-panel p-6 max-w-md w-full">
            <h3 className="font-display text-h2 text-primary mb-2">How much have you saved toward this vision?</h3>
            <p className="text-body text-secondary mb-4">
              Current: <span className="figure text-primary">{formatCurrency(displayVision.current_amount)}</span>
              {' '}/ {formatCurrency(displayVision.target_amount)}
            </p>
            <div className="mb-4">
              <label className="block text-label text-secondary mb-2">Amount saved so far ($)</label>
              <input
                type="number"
                min="0"
                step="1"
                value={newProgressAmount}
                onChange={(e) => setNewProgressAmount(e.target.value)}
                placeholder="Enter amount saved"
                className="w-full input-editorial figure"
              />
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowUpdateProgressModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleUpdateProgress}
                className="btn-primary flex-1"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Achieved Modal */}
      {showMarkAchievedModal && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center p-4 z-[1000]">
          <div className="surface-panel p-6 max-w-md w-full">
            <h3 className="font-display text-h2 text-primary mb-4">Mark as achieved</h3>
            <p className="text-body text-secondary mb-6">
              Are you sure you want to mark this vision as achieved? This will move it to your achieved visions.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowMarkAchievedModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMarkAchieved}
                className="btn-primary flex-1"
              >
                Mark Achieved
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisionDetailPage;